import { cx } from '@emotion/css';
import { DragHandle, DragIndicatorRounded } from '@mui/icons-material';
import { FC, ReactNode, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import * as S from './styles';

type DraggableListItemProps = {
  children: ReactNode;
  id: number;
  index: number;
  disabled?: boolean;
  className?: string;
  moveCard: (id: number, to: number) => void;
  onDropEnd: ({ id, index }: { id: number; index: number }) => void;
};

export const DraggableListItem: FC<DraggableListItemProps> = ({
  id,
  children,
  index,
  disabled,
  moveCard,
  onDropEnd,
  className,
}) => {
  const ref = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = (item as unknown as { index: number }).index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = (
        ref.current as unknown as {
          getBoundingClientRect: () => ClientRect;
        }
      )?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = (clientOffset?.y || 0) - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      (item as unknown as { index: number }).index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: 'card',
    item: { type: 'card', id, index },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (dropResult, monitor) => {
      onDropEnd(dropResult as { id: number; index: number });
    },
  }));

  drag(drop(ref));

  return (
    <S.DragItem
      data-testid={id}
      data-test={'draggable-list-item'}
      // @TODO: we need this?
      // ref={(node) => preview(drop(node))}
      isDragging={isDragging}
      className={cx(
        'rounded-lg width-100 bg-neutral-50 shadow-2 layout-row layout-align-start-center p-3',
        { 'shadow-4': isDragging },
        className,
      )}>
      <S.DragHandle
        ref={disabled ? null : ref}
        isDragging={isDragging}
        className="mr-2 layout-column layout-align-center-center"
        data-handler-id={handlerId}>
        <DragIndicatorRounded className="text-secondary-300" />
      </S.DragHandle>
      {children}
    </S.DragItem>
  );
};
